import { ReactNode, useState } from 'react';
import { Check, ChevronDown, ChevronRight, CircleCheck } from 'lucide-react';

import { ChatModel } from '@/api/models';
import { chatModels } from '@/constants/models';
import { applyPersistedModelSelection, getEffortLabel, getEffortLevels, getSelectedEffort, withReasoningEffort } from '@/utils/modelSelection';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Slider } from '@/components/ui/slider';

// Model catalogue folders, in the order they are listed in the Advanced > Model menu.
// const MODEL_FOLDERS = ['', 'Anthropic', 'Additional models'];
const MODEL_FOLDERS = ['', 'Anthropic'];
// The app's phone breakpoint, matching the composer's own max-[700px] rules.
const NARROW_VIEWPORT_QUERY = '(max-width: 700px)';

type AdvancedSection = 'model' | 'effort' | null;

interface ModelSelectorProps {
  currentModel: ChatModel | null;
  /** Switches model. Outside a group chat this starts a new conversation. */
  onModelChange: (model: ChatModel) => void;
  /** Changes reasoning effort only, which must never reset the conversation. */
  onEffortChange: (model: ChatModel) => void;
  getBrandLogo: (brand: string) => string;
  onTrackEvent: (item: string, text: string) => void;
  /** Keeps the compact composer usable on small screens by showing only the brand logo. */
  hideLabelOnMobile?: boolean;
}

// Bounded by the space Radix measured between the trigger and the viewport edge, so an
// expanded model list scrolls inside the menu instead of running off a phone screen.
const menuContentClass = 'w-[248px] max-w-[calc(100vw-1rem)] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto rounded-3xl border border-black/10 bg-secondary p-3 text-black shadow-short dark:border-white/10 dark:text-white';
// Capped against the viewport so the flyout still fits once it flips side on a phone.
const subContentClass = 'w-[210px] max-w-[calc(100vw-1rem)] rounded-[22px] border border-black/10 bg-secondary p-1.5 text-black shadow-short dark:border-white/10 dark:text-white';
const rowClass = 'flex w-full cursor-pointer items-center gap-2 rounded-[16px] px-2.5 py-2 text-[13px] outline-none transition hover:bg-black/[0.04] focus:bg-black/[0.04] data-[state=open]:bg-black/[0.04] dark:hover:bg-white/[0.05] dark:focus:bg-white/[0.05] dark:data-[state=open]:bg-white/[0.05]';
const modelItemClass = 'flex cursor-pointer items-center gap-2 rounded-[16px] px-2.5 py-2 text-[13px] hover:bg-black/[0.04] focus:bg-black/[0.04] dark:hover:bg-white/[0.05] dark:focus:bg-white/[0.05]';
// The slider and the Advanced rows take turns in the same slot, so each fades in on arrival.
const sectionClass = 'animate-in fade-in-0 slide-in-from-top-1 duration-200 ease-out';
const mutedTextClass = 'text-black/55 dark:text-white/75';
const selectedTextClass = 'text-sky-700 dark:text-sky-300';

const ModelSelector = ({ currentModel, onModelChange, onEffortChange, getBrandLogo, onTrackEvent, hideLabelOnMobile = false }: ModelSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [openSection, setOpenSection] = useState<AdvancedSection>(null);
  const isNarrowViewport = typeof window !== 'undefined' && window.matchMedia(NARROW_VIEWPORT_QUERY).matches;

  const effortLevels = getEffortLevels(currentModel);
  const selectedEffort = getSelectedEffort(currentModel);
  const selectedEffortIndex = Math.max(0, effortLevels.indexOf(selectedEffort));
  // The pill reads as the effort while the default model is in use; a deliberate switch to
  // another model takes the label over so that choice is never hidden behind Advanced.
  const triggerLabel = currentModel?.default && selectedEffort ? getEffortLabel(selectedEffort) : (currentModel?.name || '');

  // Closing always returns the menu to its resting state: slider first, Advanced collapsed.
  const closeMenu = () => {
    setIsOpen(false);
    setIsAdvancedOpen(false);
    setOpenSection(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeMenu();
      return;
    }
    setIsOpen(true);
    onTrackEvent('models', 'Model Dropdown');
  };

  const handleEffortChange = (effort: string) => {
    if (!currentModel || effort === selectedEffort) return;
    onTrackEvent('model_effort', 'Model Effort');
    onEffortChange(withReasoningEffort(currentModel, effort));
  };

  const handleModelChange = (model: ChatModel) => {
    closeMenu();
    if (model.id === currentModel?.id) return;
    onModelChange(applyPersistedModelSelection(model) || model);
  };

  const renderModelItem = (model: ChatModel) => {
    const isCurrentModel = model.id === currentModel?.id;

    return (
      <DropdownMenuItem key={model.id} className={`${modelItemClass} ${isCurrentModel ? selectedTextClass : ''}`} onClick={() => handleModelChange(model)}>
        <img src={getBrandLogo(model.brand || '')} className="rounded" width="14" />
        <span className="truncate">{model.name}</span>
        {isCurrentModel ? <CircleCheck size={16} className="ms-auto shrink-0" /> : null}
      </DropdownMenuItem>
    );
  };

  const modelItems = MODEL_FOLDERS.map((folder, index) => (
    <div key={folder || 'default'}>
      {index > 0 ? <div className="divider my-0"></div> : null}
      {chatModels.filter((model) => model.folder === folder).map((model) => renderModelItem(model))}
    </div>
  ));

  const effortItems = effortLevels.map((effort) => (
    <DropdownMenuItem
      key={effort}
      className={`${modelItemClass} ${effort === selectedEffort ? selectedTextClass : ''}`}
      onClick={() => handleEffortChange(effort)}
    >
      <span>{getEffortLabel(effort)}</span>
      {effort === selectedEffort ? <Check size={16} className="ms-auto shrink-0" /> : null}
    </DropdownMenuItem>
  ));

  // A side flyout has nowhere to go on a phone -- the menu itself takes most of the width --
  // so below the app's 700px breakpoint the lists expand in place instead.
  const renderAdvancedSection = (key: Exclude<AdvancedSection, null>, label: string, value: string, items: ReactNode) => {
    if (!isNarrowViewport) {
      return (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={rowClass}>
            <span>{label}</span>
            <span className={`ms-auto truncate text-[13px] ${mutedTextClass}`}>{value}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContentClass} sideOffset={8}>{items}</DropdownMenuSubContent>
        </DropdownMenuSub>
      );
    }

    const isSectionOpen = openSection === key;

    return (
      <div>
        <button type="button" className={rowClass} onClick={() => setOpenSection(isSectionOpen ? null : key)}>
          <span>{label}</span>
          <span className={`ms-auto truncate text-[13px] ${mutedTextClass}`}>{value}</span>
          {isSectionOpen ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
        </button>
        {isSectionOpen ? <div className={sectionClass}>{items}</div> : null}
      </div>
    );
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger className="inline-flex h-8 items-center gap-1 rounded-full px-3 py-1.5 text-[14px] leading-5 text-black/75 transition hover:bg-black/[0.04] dark:text-white/85 dark:hover:bg-white/[0.05] tour-step-12">
        <div className="flex items-center gap-2">
          <img src={getBrandLogo(currentModel?.brand || '')} className="rounded" width="14" />
          <span className={hideLabelOnMobile ? 'max-[700px]:hidden' : undefined}>{triggerLabel}</span>
          <ChevronDown size={14} className="text-black/40 dark:text-white/55" />
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent className={menuContentClass} align="end">
        {effortLevels.length && !isAdvancedOpen ?
          // The slider keeps its own key events so the menu's arrow-key navigation does not swallow them.
          <div className={sectionClass} onKeyDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-1.5 pb-1">
              <span className={`text-[11px] ${mutedTextClass}`}>Reasoning effort</span>
              <span className="text-[11px] text-black dark:text-white">{getEffortLabel(selectedEffort)}</span>
            </div>
            <Slider
              showTicks
              className="px-1.5 pt-1"
              min={0}
              max={effortLevels.length - 1}
              step={1}
              value={[selectedEffortIndex]}
              onValueChange={([index]) => handleEffortChange(effortLevels[index])}
              aria-label="Reasoning effort"
            />
            <div className="divider my-1"></div>
          </div>
        : null}

        <button type="button" className={`${rowClass} ${mutedTextClass}`} onClick={() => { setIsAdvancedOpen((open) => !open); setOpenSection(null); }}>
          <span>Advanced</span>
          {isAdvancedOpen ? <ChevronDown size={14} className="ms-auto shrink-0" /> : <ChevronRight size={14} className="ms-auto shrink-0" />}
        </button>

        {isAdvancedOpen ?
          <div className={`pt-0.5 ${sectionClass}`}>
            {renderAdvancedSection('model', 'Model', currentModel?.name || '', modelItems)}
            {effortLevels.length ? renderAdvancedSection('effort', 'Effort', getEffortLabel(selectedEffort), effortItems) : null}
          </div>
        : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ModelSelector;
